import {invoke, isTauri} from "@tauri-apps/api/core";

export const ENV_URL_STORAGE_KEY = "lumids:environment-url";
export const DEFAULT_ENVIRONMENT_URL: string =
    import.meta.env.VITE_ENVIRONMENT || "https://de.amstel.lumids.com";

/* manual entry — any host the browser can reach: FQDN, bare hostname, raw IP, with optional port; ws(s) → http(s) */
export const normalizeEnvironmentUrl = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const withProtocol = trimmed.includes("://") ? trimmed : `https://${trimmed}`;

    try {
        const parsed = new URL(withProtocol);
        if (parsed.protocol === "ws:") parsed.protocol = "http:";
        if (parsed.protocol === "wss:") parsed.protocol = "https:";
        if (!parsed.hostname || parsed.origin === "null") return null;
        return parsed.origin;
    } catch {
        return null;
    }
};

/* automatic inference — the page's own origin, only when served over https (PWA on the backend host);
   http dev servers and desktop shells return null so the auth page asks for the backend instead */
export const getPageEnvironmentUrl = (): string | null => {
    if (typeof window === "undefined") return null;
    const {protocol, origin} = window.location;
    return protocol === "https:" ? origin : null;
};

export const getStoredEnvironmentUrl = (): string | null => {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem(ENV_URL_STORAGE_KEY);
    if (!stored) return null;
    return normalizeEnvironmentUrl(stored);
};

/* inferred page origin always wins; the stored (auth page) value only matters where nothing can be inferred */
export const resolveEnvironmentUrl = (): string =>
    getPageEnvironmentUrl() ?? getStoredEnvironmentUrl() ?? DEFAULT_ENVIRONMENT_URL;

export const syncEnvironmentUrlToNativeUpdater = async (environmentUrl: string | null): Promise<void> => {
    if (!isTauri()) return;
    try {
        await invoke("set_updater_environment_url", {environmentUrl});
    } catch (error) {
        console.warn("[EnvironmentUrl] Failed to sync environment URL to native updater", error);
    }
};
